import { SyntheticEvent, useMemo } from "react";
import Autocomplete, { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

export interface CodeOption {
  code: string;
  label: string;
}

interface CodeMultiSelectProps {
  label: string;
  options: CodeOption[];
  /** The selected codes; a code the options do not hold is still shown, as itself. */
  value: string[];
  onChange: (codes: string[]) => void;
  helperText?: string;
  disabled?: boolean;
}

/** Chips beyond this many collapse into "+N" while the field is not focused. */
const COLLAPSED_CHIP_LIMIT = 6;

const getOptionLabel = (option: CodeOption) => option.label;

const isSameOption = (option: CodeOption, selected: CodeOption) =>
  option.code === selected.code;

/** A multi-select over a list of codes, rendered as chips, that hands back the codes. */
export default function CodeMultiSelect({
  label,
  options,
  value,
  onChange,
  helperText,
  disabled = false,
}: CodeMultiSelectProps) {
  const selected = useMemo(() => {
    const optionByCode = new Map(options.map((option) => [option.code, option]));
    return value.map((code) => optionByCode.get(code) ?? { code, label: code });
  }, [options, value]);

  const handleChange = (_event: SyntheticEvent, next: CodeOption[]) => {
    onChange(next.map((option) => option.code));
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params} size="small" label={label} helperText={helperText} />
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isSameOption}
      filterSelectedOptions
      disableCloseOnSelect
      limitTags={COLLAPSED_CHIP_LIMIT}
      disabled={disabled}
      noOptionsText="ไม่พบข้อมูล"
      renderInput={renderInput}
    />
  );
}
